import React, { useMemo, useState, useEffect } from "react";
import { db } from "../../firebase";
import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  query, 
  orderBy,
  Timestamp 
} from "firebase/firestore";
import "./big-event.css";

/** Helper: safe slug */
function toSlug(s = "") {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/** Small global nav bridge set in App.jsx */
function navigateTo(name) {
  if (
    typeof window !== "undefined" &&
    typeof window.navigateToCRM === "function"
  ) {
    window.navigateToCRM(name);
  }
}

export default function BigEventBuilder() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHidden, setShowHidden] = useState(false);
  const [dashMode, setDashMode] = useState("current"); // 'current' | 'active'
  
  // Create modal
  const [openCreate, setOpenCreate] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    startLocal: "", // yyyy-MM-ddTHH:mm (HTML datetime-local)
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
    name: "",
    locationName: "",
    linkSlug: "",
    contactEmail: "",
    trackBy: {
      MD: false,
      SMD: false,
      CEO: false,
      EVC: false,
      DONT: false,
    },
  });

  const urlPrefix = "https://wealthbuildersevents.com/";

  // Load events from Firebase
  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const eventsRef = collection(db, "big_events");
      const q = query(eventsRef, orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      
      const eventsList = [];
      snapshot.forEach((doc) => {
        eventsList.push({ id: doc.id, ...doc.data() });
      });
      
      setEvents(eventsList);
    } catch (error) {
      console.error("Error loading events:", error);
    } finally {
      setLoading(false);
    }
  };

  const activeEvents = useMemo(
    () => events.filter((e) => e.active && (!e.hidden || showHidden)),
    [events, showHidden]
  );

  const currentEvent = useMemo(() => activeEvents[0] || null, [activeEvents]);

  const filteredRows = useMemo(() => {
    return events.filter((e) => (showHidden ? true : !e.hidden));
  }, [events, showHidden]);

  const toggleHide = async (id) => {
    try {
      const event = events.find((e) => e.id === id);
      if (!event) return;

      const eventRef = doc(db, "big_events", id);
      await updateDoc(eventRef, {
        hidden: !event.hidden,
        updatedAt: Timestamp.now(),
      });

      // Update local state
      setEvents((prev) =>
        prev.map((e) => (e.id === id ? { ...e, hidden: !e.hidden } : e))
      );
    } catch (error) {
      console.error("Error toggling hide:", error);
      alert("Failed to update event. Please try again.");
    }
  };

  function gotoPurchases(eventId) {
    // Stash filter so Purchases can read it
    sessionStorage.setItem("purchasesEventFilter", eventId);
    navigateTo("Purchases");
  }

  function gotoEdit(eventId) {
    sessionStorage.setItem("editingEventId", eventId);
    navigateTo("Edit Event");
  }

  const submitCreate = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Generate event number based on existing count
      const number = String(events.length + 1).padStart(3, "0");
      
      // Create new event document
      const eventRef = doc(collection(db, "big_events"));
      const eventId = eventRef.id;

      const startISO = form.startLocal
        ? new Date(form.startLocal).toISOString()
        : "";

      const newEvent = {
        number,
        title: form.name || "Untitled Event",
        seats: 0,
        startISO,
        timezone: form.timezone,
        linkSlug: form.linkSlug || toSlug(form.name || "event"),
        price: 0,
        hidden: false,
        active: true,
        locationName: form.locationName,
        contactEmail: form.contactEmail,
        trackBy: { ...form.trackBy },
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(eventRef, newEvent);

      // Add to local state
      setEvents((prev) => [{ id: eventId, ...newEvent }, ...prev]);

      setOpenCreate(false);
      setSaving(false);

      // Reset form
      setForm({
        startLocal: "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
        name: "",
        locationName: "",
        linkSlug: "",
        contactEmail: "",
        trackBy: {
          MD: false,
          SMD: false,
          CEO: false,
          EVC: false,
          DONT: false,
        },
      });

      // Jump to edit page for the newly created event
      sessionStorage.setItem("editingEventId", eventId);
      navigateTo("Edit Event");
    } catch (error) {
      console.error("Error creating event:", error);
      alert("Failed to create event. Please try again.");
      setSaving(false);
    }
  };

  const dashCards = useMemo(() => {
    const base =
      dashMode === "current" && currentEvent ? [currentEvent] : activeEvents;
    const totalSeats = base.reduce((a, b) => a + (b.seats || 0), 0);
    const avgPrice =
      base.length > 0
        ? Math.round(
            (base.reduce((a, b) => a + (b.price || 0), 0) / base.length) * 100
          ) / 100
        : 0;
    return {
      count: base.length,
      seats: totalSeats,
      avgPrice,
      soonestDate:
        base.length > 0
          ? base
              .slice()
              .sort((a, b) => new Date(a.startISO) - new Date(b.startISO))[0]
          : null,
    };
  }, [dashMode, currentEvent, activeEvents]);

  if (loading) {
    return (
      <div className="be-wrap">
        <div className="loading-center">
          <div className="loading-spinner"></div>
          <p>Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="be-wrap">
      {/* Header */}
      <div className="be-header compact">
        <div>
          <h1 className="be-title">Big Event Builder</h1>
          <p className="be-subtitle">Create and manage your events by team.</p>
        </div>

        <div className="be-header-right">
          <label className="toggle-wrap">
            <span>Dashboard:</span>
            <select
              className="input input-compact"
              value={dashMode}
              onChange={(e) => setDashMode(e.target.value)}
            >
              <option value="current">Current Event</option>
              <option value="active">Active Events</option>
            </select>
          </label>

          <button
            className="gold-button gold-glow"
            onClick={() => setOpenCreate(true)}
          >
            + Create Big Event
          </button>
        </div>
      </div>

      {/* Mini Dashboard */}
      <div className="be-mini-dash">
        <div className="kpi-card mini">
          <div className="kpi-label">
            {dashMode === "current" ? "Current Event" : "Active Events"}
          </div>
          <div className="kpi-value">{dashCards.count}</div>
          <div className="kpi-muted">
            {dashMode === "current" ? "Selected" : "Total active"}
          </div>
        </div>
        <div className="kpi-card mini">
          <div className="kpi-label">Butts in Seats</div>
          <div className="kpi-value">{dashCards.seats.toLocaleString()}</div>
          <div className="kpi-muted">Live seats</div>
        </div>
        <div className="kpi-card mini">
          <div className="kpi-label">Avg Ticket Price</div>
          <div className="kpi-value">
            ${dashCards.avgPrice.toLocaleString()}
          </div>
          <div className="kpi-muted">Across selected</div>
        </div>
        <div className="kpi-card mini">
          <div className="kpi-label">Soonest Start</div>
          <div className="kpi-value">
            {dashCards.soonestDate
              ? new Date(dashCards.soonestDate.startISO).toLocaleDateString()
              : "—"}
          </div>
          <div className="kpi-muted">
            {dashCards.soonestDate ? dashCards.soonestDate.timezone : ""}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="be-row between">
        <label className="checkbox-inline">
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
          />
          Show hidden
        </label>
      </div>

      {/* Table */}
      <div className="be-card">
        <div className="be-table-wrap">
          <table className="be-table compact">
            <thead>
              <tr>
                <th style={{ width: 70 }}>#</th>
                <th>Event Title</th>
                <th style={{ width: 120 }}>Seats</th>
                <th style={{ width: 160 }}>Date</th>
                <th style={{ width: 180 }}>Event Page</th>
                <th style={{ width: 110 }}>Price</th>
                <th style={{ width: 240 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => {
                const dateLabel = r.startISO
                  ? new Date(r.startISO).toLocaleString()
                  : "—";
                const linkPreview = r.linkSlug
                  ? `${urlPrefix}${r.linkSlug}`
                  : "—";
                return (
                  <tr key={r.id} className={r.hidden ? "row-hidden" : ""}>
                    <td className="mono">{r.number}</td>
                    <td className="ellipsis">{r.title}</td>
                    <td>{(r.seats || 0).toLocaleString()}</td>
                    <td>
                      <div className="two-line">
                        <span>{dateLabel}</span>
                        <span className="muted">{r.timezone}</span>
                      </div>
                    </td>
                    <td>
                      {r.linkSlug ? (
                        <a 
                          href={linkPreview} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="chip chip-link" 
                          title={linkPreview}
                          style={{ textDecoration: 'none', display: 'inline-block' }}
                        >
                          Open Page
                        </a>
                      ) : (
                        <span className="muted">Not linked</span>
                      )}
                    </td>
                    <td>${r.price || 0}</td>
                    <td>
                      <div className="action-row">
                        <button
                          className="btn subtle"
                          onClick={() => gotoPurchases(r.id)}
                        >
                          Purchases
                        </button>
                        <button
                          className="btn subtle"
                          onClick={() => gotoEdit(r.id)}
                        >
                          Edit
                        </button>
                        <button
                          className={`btn subtle ${r.hidden ? "" : "danger"}`}
                          onClick={() => toggleHide(r.id)}
                        >
                          {r.hidden ? "Unhide" : "Hide"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-cell">
                    {events.length === 0 
                      ? "No events yet. Create your first event to get started!"
                      : "No events to show. Try enabling 'Show hidden'."
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {openCreate && (
        <div className="modal-backdrop" onClick={() => setOpenCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Create Big Event</h3>
            </div>
            <form className="modal-body" onSubmit={submitCreate}>
              {/* Date/Time/TZ */}
              <div className="grid-2">
                <label className="field">
                  <span className="field-label">Event Beginning (local)</span>
                  <div className="with-icon">
                    <input
                      type="datetime-local"
                      className="input"
                      value={form.startLocal}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, startLocal: e.target.value }))
                      }
                      required
                    />
                    <button
                      type="button"
                      className="icon-btn"
                      title="Open calendar"
                      onClick={(e) => {
                        // Focus triggers native picker on many browsers
                        e.currentTarget.previousSibling?.showPicker?.();
                        e.currentTarget.previousSibling?.focus?.();
                      }}
                    >
                      📅
                    </button>
                  </div>
                </label>

                <label className="field">
                  <span className="field-label">Time Zone</span>
                  <select
                    className="input"
                    value={form.timezone}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, timezone: e.target.value }))
                    }
                  >
                    {[
                      "America/Los_Angeles",
                      "America/Denver",
                      "America/Chicago",
                      "America/New_York",
                      "America/Toronto",
                      "America/Phoenix",
                      "America/Bogota",
                      "America/Sao_Paulo",
                      "Europe/London",
                      "Europe/Paris",
                    ].map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {/* Name */}
              <label className="field">
                <span className="field-label">Event Name</span>
                <input
                  className="input"
                  placeholder="e.g., Breaking Barriers 2025"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm((s) => ({
                      ...s,
                      name,
                      linkSlug: s.linkSlug ? s.linkSlug : toSlug(name),
                    }));
                  }}
                  required
                />
              </label>

              {/* Location with tooltip */}
              <label className="field">
                <span className="field-label">
                  Event Location Name
                  <span
                    className="info-dot"
                    title="Use the venue/location name so buyers can find it easily online."
                  >
                    ⓘ
                  </span>
                </span>
                <input
                  className="input"
                  placeholder="e.g., Salt Palace Convention Center, Salt Lake City"
                  value={form.locationName}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, locationName: e.target.value }))
                  }
                  required
                />
              </label>

              {/* Shortcut */}
              <label className="field">
                <span className="field-label">Shortcut</span>
                <div className="shortcut-wrap">
                  <span className="prefix">wealthbuildersevents.com/</span>
                  <input
                    className="input no-pad"
                    placeholder="your-shortcut"
                    value={form.linkSlug}
                    onChange={(e) =>
                      setForm((s) => ({
                        ...s,
                        linkSlug: toSlug(e.target.value),
                      }))
                    }
                  />
                </div>
                <div className="muted small">
                  Full link: {urlPrefix}
                  <strong>{form.linkSlug || toSlug(form.name)}</strong>
                </div>
              </label>

              {/* Contact Email (soon) */}
              <label className="field">
                <span className="field-label">
                  Contact Email <span className="tag-soon">Available soon</span>
                </span>
                <input
                  className="input"
                  placeholder="support@yourdomain.com"
                  value={form.contactEmail}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, contactEmail: e.target.value }))
                  }
                  disabled
                />
              </label>

              {/* Track By */}
              <fieldset className="field">
                <legend className="field-label">Track By</legend>
                <div className="checks">
                  {["MD", "SMD", "CEO", "EVC", "DONT"].map((k) => (
                    <label key={k} className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={!!form.trackBy[k]}
                        onChange={(e) =>
                          setForm((s) => ({
                            ...s,
                            trackBy: { ...s.trackBy, [k]: e.target.checked },
                          }))
                        }
                      />
                      {k === "DONT" ? "DON'T TRACK" : k}
                    </label>
                  ))}
                </div>
                <div className="muted small">
                  These fields will appear on the checkout form for buyer
                  attribution.
                </div>
              </fieldset>

              <div className="modal-foot">
                <button
                  type="button"
                  className="btn subtle"
                  onClick={() => setOpenCreate(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="gold-button"
                  disabled={saving}
                >
                  {saving ? "Creating..." : "Create Event"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
